// ./src/components/features/CreateEventWizard/CreateEventWizard.tsx
//
// The Create Event wizard shell. Owns the cross-step draft, persists it to
// localStorage (restore-on-reload), guards accidental exit (back-swipe + reload),
// and drives step navigation. Individual steps are thin panels that read/write the
// draft through WizardContext.
//
// ⚠⚠ ROADMAP A2b #62 P6a (owner, 2026-09-29): THE EVENT IS CREATED WHEN THE ESSENTIALS ARE IN — leaving the Guests
// step (type, name, date/time, where, how guests join). Every later step writes to the REAL event, and the host can
// FINISH from any of them. Going back and changing an essential re-saves it to the same event (never a second one:
// `createdEventId` is persisted with the draft, so a reload or a resumed phone keeps adding to the one event).
//
// Mobile-first: a sticky header (title + progress + close), a scrollable body, and
// a sticky footer with Back / Next. The same layout scales up cleanly on desktop.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Group, Stack, Text, Title, Progress, ActionIcon, Button, Container, Alert } from '@mantine/core';
import { StackedModal } from '@em/ui';
import { deadlinePickerValue } from '@em/utils';
import { OP_RESULT_CODES, OpResult } from '@sdflc/api-helpers';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES, getRoutePath } from '@/config';
import { refusalReason } from '@/utils';
import { useCreateGate, useDeadlineFollowsEnd, useToast } from '@/hooks';
import { useOptionalAccountPreferenceContext } from '@/contexts/AccountPreferenceContext';
import { useApp } from '@/contexts/AppContext';
import { CurrentEventProvider } from '@/contexts';
import { EventService, EventHighlightService, EventTimeSlotService, Event, EventFeatureUsage, TicketType, TicketTypeService } from '@/utils/api';
import { AppIcon, ConfirmModal, ErrorAlert } from '@/components/generic';
import { evaluateSetupRules } from '@/config/eventSetup.generated';

import { WizardProvider } from './WizardContext';
import {
  essentialsInput,
  featuresAfterRules,
  highlightChanges,
  initialFeatures,
  mediaInput,
  quickEventInput,
  quickEventMode,
  quickFeatures,
  quickFinishState,
  quickPublishHold,
  quickStepOfRefusal,
  quickTicketInput,
  quickTicketMissing,
  withFeatures,
  QUICK_FINISH_CHECKING,
  QuickPublishHold,
} from './wizardSetup';
import { EventCreatedScreen, CreateResult } from './components/EventCreatedScreen';
import {
  EventWizardDraft,
  emptyEventWizardDraft,
  EventWizardContextValue,
  WIZARD_STEP_META,
  WizardStepKey,
  EVENT_FORMAT,
  EventMode,
  ESSENTIALS_LAST_STEP,
  visibleWizardSteps,
  resolveWizardStep,
  adjacentWizardStep,
  SCHEDULE_MODE,
  QUICK_KIND,
  quickKindOf,
  quickStepsOf,
} from './wizardTypes';
import { useOptionalEventSetupCatalogContext } from '@/contexts/EventSetupCatalogContext';
import type { EventSetupType } from '@/utils/api';
import { EventCategoryGroup } from './data/eventTypeGroups';
import { draftDeadlineCeiling } from './draftToEventInput';
import { useEventWizardDraft } from './useEventWizardDraft';
import { applyAccountDefaults } from './accountDefaults';
import { useWizardExitGuard } from './useWizardExitGuard';
import { useWizardFunnel } from './useWizardFunnel';
import { useCardPayments } from '@/components/features/CardPayments';
import { EventTypeStep } from './steps/EventTypeStep';
import { BasicsStep } from './steps/BasicsStep';
import { WhereStep } from './steps/WhereStep';
import { GuestsStep } from './steps/GuestsStep';
import { MediaStep } from './steps/MediaStep';
import { FeaturesStep } from './steps/FeaturesStep';
import { TicketsStep } from './steps/TicketsStep';
import { QuickTicketStep } from './steps/QuickTicketStep';
import { VouchersStep } from './steps/VouchersStep';
import { QuestionsStep } from './steps/QuestionsStep';
import { InvitesStep } from './steps/InvitesStep';
import { ChannelsStep } from './steps/ChannelsStep';
import { SignupsStep } from './steps/SignupsStep';
import { PollsStep } from './steps/PollsStep';
import { GamesStep } from './steps/GamesStep';
import { PoliciesStep } from './steps/PoliciesStep';
import { DrawsStep } from './steps/DrawsStep';
import { ArtistsStep } from './steps/ArtistsStep';
import { PoisStep } from './steps/PoisStep';
import { AccommodationsStep } from './steps/AccommodationsStep';
import { VolunteersStep } from './steps/VolunteersStep';
import { PartnersStep } from './steps/PartnersStep';
import { TimelineStep } from './steps/TimelineStep';
import { TasksStep } from './steps/TasksStep';
import { BudgetStep } from './steps/BudgetStep';
import { WebsiteStep } from './steps/WebsiteStep';
import { ScheduleStep } from './steps/ScheduleStep';
import { GiftsStep } from './steps/GiftsStep';
import { WEBSITE_BLOCK_OPTIONS, orderAfterCreate, planWebsiteBlocks, textKindOfFormat, websiteKeysToAdd } from './websiteBlocks';
import { applyGiftLinks, applyWebsitePlan } from './websiteBlocksApply';
import { SIGNUP_LIST_FEATURE, giftLinkButtons, giftLinkProblems, isGiftList } from './giftRegistry';
import { ensureRecurringDates } from './scheduleApply';
import { PlaceholderStep } from './steps/PlaceholderStep';

// ⚠ Positions in the FULL registry — for "is this step before/after the essentials", never for navigation (which goes
// by key through the VISIBLE steps: see `resolveWizardStep`).
const ESSENTIALS_LAST_INDEX = WIZARD_STEP_META.findIndex((s) => s.key === ESSENTIALS_LAST_STEP);
const registryIndexOf = (key: WizardStepKey): number => WIZARD_STEP_META.findIndex((s) => s.key === key);
// Mirrors EventCreatedScreen — the API takes the publish state as a string enum.
const EVENT_STATUS_PUBLISHED = 'published';

/** Seed the category fields of a draft from a chosen type. */
const applyType = (draft: EventWizardDraft, type: EventSetupType): EventWizardDraft => ({
  ...draft,
  eventCategoryId: type.id,
  eventCategoryCode: type.code,
  eventCategoryGroup: type.categoryGroup as EventCategoryGroup,
});

/** Minimum requirement to advance from a given step. */
const canAdvanceFrom = (stepKey: WizardStepKey, draft: EventWizardDraft): boolean => {
  switch (stepKey) {
    case 'type':
      return !!draft.eventCategoryId;
    case 'basics':
      // #139 — the quick path goes live on Finish, so it needs its date here (the full path may set one later).
      return draft.title.trim().length > 0 && (!draft.quick || !!draft.startsAt);
    case 'location': {
      const hasVenue = !!draft.venueId;
      const hasUrl = draft.onlineMeetingUrl.trim().length > 0;
      if (draft.eventFormat === EVENT_FORMAT.ONLINE) {
        return hasUrl;
      }
      if (draft.eventFormat === EVENT_FORMAT.HYBRID) {
        return hasVenue || hasUrl;
      }
      return hasVenue; // in_person
    }
    case 'guests':
      return !!draft.eventMode;
    case 'tickets':
      // #148 — the quick ticket sale's ticket: its name, price, currency and how many (presence only). The full Tickets
      // step writes as it goes, so it never gates.
      return quickKindOf(draft) !== QUICK_KIND.TICKETS || quickTicketMissing(draft).length === 0;
    default:
      // Placeholder steps don't gate progress in the mockup.
      return true;
  }
};

/** A draft is worth confirming-before-leaving once it holds real content. */
const draftIsDirty = (draft: EventWizardDraft): boolean =>
  !!draft.eventCategoryId || draft.title.trim().length > 0 || draft.subtitle.trim().length > 0;

export const CreateEventWizard: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);

  const eventsHome = getRoutePath('events');
  const { savedState, hasSavedDraft, save, clear } = useEventWizardDraft();
  // A2b #53 — what this account's new events start from. Optional: the wizard must still work when
  // the provider is absent (and for an account that has saved nothing, which is most of them).
  const accountPreference = useOptionalAccountPreferenceContext();
  // #198 a — the quick Finish publishes only for a publisher whose email is confirmed.
  const { user } = useApp();

  // Deep-link: ?category=<code|id> preselects the type and jumps straight to
  // Basics (skipping the type step), provided the value matches a known category.
  const [searchParams] = useSearchParams();
  // #62 — the link's category is RESOLVED against the catalog once it has loaded (below); whether this is a deep-link
  // arrival at all is known at once, so the restore prompt and the starting step are decided at mount as before.
  const deepLinkRaw = useMemo<string | null>(() => searchParams.get('category')?.trim().toLowerCase() || null, [searchParams]);
  const deepLinkType = deepLinkRaw;

  const [draft, setDraft] = useState<EventWizardDraft>(() => emptyEventWizardDraft());
  // ⚠ The CURRENT STEP IS A KEY, not an index: steps come and go with the draft (Tickets only on a ticketed event), and
  // an index would silently point at a different screen when one appears before it.
  const [currentKey, setCurrentKey] = useState<WizardStepKey>(() => (deepLinkType ? 'basics' : 'type'));

  // Apply the deep-linked type ONCE the catalog is in. An unknown code sends the host back to the type step rather
  // than leaving them on Basics with no type (which the Next gate would then refuse with nothing on screen to fix).
  const catalog = useOptionalEventSetupCatalogContext();
  const deepLinkAppliedRef = useRef(false);

  useEffect(() => {
    if (!deepLinkRaw || deepLinkAppliedRef.current || !catalog?.isLoaded) {
      return;
    }

    deepLinkAppliedRef.current = true;

    const type = catalog.findType(deepLinkRaw);

    if (type) {
      setDraft((d) => applyType(d, type));
    } else {
      setCurrentKey('type');
    }
  }, [deepLinkRaw, catalog]);

  // Restore prompt: offered once on mount when a saved draft exists — but an
  // explicit ?category deep-link is a fresh, intentional start, so it wins.
  const [restoreOpen, setRestoreOpen] = useState(() => !deepLinkType && hasSavedDraft);
  // Exit confirm: opened when the guard intercepts a back/swipe, or via the close button.
  const [exitOpen, setExitOpen] = useState(false);
  // Terminal result screen (success/failure) shown after a create attempt.
  const [result, setResult] = useState<CreateResult | null>(null);
  // Latches the moment the account defaults are applied (or deliberately skipped).
  const seededRef = useRef(false);
  // #62 P7 — the host chose "Continue" on the restore prompt (for the funnel's `wizard_start`).
  const [resumed, setResumed] = useState(false);

  // What the setup rules say about this draft — which setup steps it gets (Vouchers when on, Questions unless a
  // save-the-date…). ⚠ The GENERATED evaluator, as everywhere else; the registry only reads its answer.
  const setupState = useMemo(() => {
    const type = catalog?.findType(draft.eventCategoryId) ?? null;
    const parent = catalog?.parentOf(type) ?? null;

    return {
      ...evaluateSetupRules(catalog?.rules ?? [], {
        eventMode: draft.eventMode,
        categoryCode: type?.code ?? null,
        parentCategoryCode: parent?.code ?? null,
        categoryGroup: parent?.categoryGroup ?? type?.categoryGroup ?? null,
        featuresOn: draft.featuresOn ?? [],
        spaceId: null,
      }),
      // #140 — the TYPE's word on gifts (the catalog's, not a rule's): it offers the Gifts step.
      typeGifts: type?.gifts ?? null,
    };
  }, [catalog, draft.eventCategoryId, draft.eventMode, draft.featuresOn]);
  const steps = useMemo(() => visibleWizardSteps(draft, setupState), [draft, setupState]);
  const stepMeta = resolveWizardStep(steps, currentKey);
  const stepKey = stepMeta.key;
  const stepIndex = steps.indexOf(stepMeta);
  const totalSteps = steps.length;
  const isDirty = draftIsDirty(draft);

  // #62 P7 — the create-event funnel (start, each step, created, finished / abandoned).
  const funnel = useWizardFunnel({
    active: !restoreOpen && !result,
    stepKey,
    stepIndex,
    stepCount: totalSteps,
    resumed,
    deepLink: !!deepLinkType,
    eventCreated: !!draft.createdEventId,
    featureCount: draft.featuresOn?.length ?? 0,
    quick: !!draft.quick,
    quickKind: quickKindOf(draft),
  });

  // Persist on every meaningful change (only once there's real content, so an
  // untouched wizard never leaves a stale "restore?" prompt behind).
  useEffect(() => {
    if (isDirty) {
      save(draft, stepKey);
    }
  }, [draft, stepKey, isDirty, save]);

  /**
   * A2b #53 — seed the draft from the account's saved defaults, ONCE.
   *
   * ⚠⚠ KEYED ON A ref THAT LATCHES, NOT ON A "loaded" BOOLEAN. The preference arrives after mount,
   * so this has to run in an effect; but an effect that can run twice would re-apply the account's
   * format over a host who has just changed it, one render later, with no explanation on screen.
   * ⚠ IT NEVER TOUCHES A RESTORED DRAFT. A restore is the host's own work from a previous session —
   * overwriting it with account defaults would lose exactly what the restore prompt promised to keep.
   */
  useEffect(() => {
    // ⚠ `hasSavedDraft` is qualified by `!deepLinkType` because a `?category=` link is a fresh,
    // intentional start: the wizard never offers to restore in that case, so a week-old draft
    // sitting in storage would otherwise be enough to silently deny the host their own defaults.
    if (seededRef.current || restoreOpen || (hasSavedDraft && !deepLinkType) || !accountPreference?.isLoaded) {
      return;
    }

    seededRef.current = true;

    if (accountPreference.preference) {
      setDraft((prev) => applyAccountDefaults(prev, accountPreference.preference));
    }
  }, [restoreOpen, hasSavedDraft, deepLinkType, accountPreference?.isLoaded, accountPreference?.preference]);

  const patchDraft = useCallback((patch: Partial<EventWizardDraft> | ((prev: EventWizardDraft) => Partial<EventWizardDraft>)) => {
    setDraft((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  }, []);

  // #137 review — a deadline sitting at the event's end moves with it when Basics changes the dates (here, in the shell:
  // the dates and the deadline are on different steps, and only the shell is mounted for both).
  const deadlineMax = draftDeadlineCeiling(draft);
  const followDeadline = useCallback((next: string) => patchDraft({ rsvpDeadline: next }), [patchDraft]);
  useDeadlineFollowsEnd(deadlineMax, deadlinePickerValue(draft.rsvpDeadline, deadlineMax), followDeadline);

  const goToStep = useCallback((key: WizardStepKey) => {
    if (registryIndexOf(key) >= 0) {
      setCurrentKey(key);
    }
  }, []);

  const goBack = useCallback(() => {
    const previous = adjacentWizardStep(steps, stepKey, -1);

    if (previous) {
      setCurrentKey(previous);
    }
  }, [steps, stepKey]);

  const goNext = useCallback(() => {
    const next = adjacentWizardStep(steps, stepKey, 1);

    if (next) {
      setCurrentKey(next);
    }
  }, [steps, stepKey]);

  // ── Exit guard (back-swipe + reload) ──────────────────────────────────────
  const handleIntercept = useCallback(() => {
    setExitOpen(true);
  }, []);

  const { leaveNow } = useWizardExitGuard({
    // Disarm once we reach the terminal result screen — the draft is created (or
    // failed and preserved), so there's nothing left to guard.
    enabled: !result,
    onIntercept: handleIntercept,
    destination: eventsHome,
  });

  const confirmLeave = useCallback(() => {
    setExitOpen(false);
    funnel.abandoned('close');
    leaveNow();
  }, [leaveNow, funnel]);

  // ── Saving to the real event (#62 P6a) ─────────────────────────────────────
  const toast = useToast();
  // #62 P6(d) — blocks the host left unticked on the website step (for the finish screen's nudge).
  const [websiteDeclined, setWebsiteDeclined] = useState<string[]>([]);
  // #140 — the Sign-up list was the GIFT list (read before the draft is cleared): the finish screen's nudge names it so.
  const [finishedGiftList, setFinishedGiftList] = useState(false);
  // #139 — why a quick event was created but is not live (its publish was refused): the finish screen says it.
  const [quickPublishError, setQuickPublishError] = useState<string | null>(null);
  // #148 — a quick ticket sale Finish kept as a DRAFT because nobody could pay yet (the finish screen says why).
  const [quickHold, setQuickHold] = useState<QuickPublishHold | null>(null);
  // #148 — the quick ticket sale: can this account take card payments (its screens and Finish share ONE read), and may
  // its plan make a ticket at all (the event does not exist yet: the account's own plan).
  const quickKind = quickKindOf(draft);
  const cardPayments = useCardPayments(quickKind === QUICK_KIND.TICKETS);
  const ticketGate = useCreateGate(FEATURE_CODES.TICKETING);
  const ticketsRefused = quickKind === QUICK_KIND.TICKETS && !ticketGate.canCreate;
  // #148 — what Finish will do, as the button and the picture step SAY it (one source, `quickFinishState`).
  const quickShown = quickFinishState(draft, cardPayments);
  const [saving, setSaving] = useState(false);
  // ⚠ Shown IN the wizard, not only toasted: a refused save leaves the host on the step with the reason in front of them.
  const [saveError, setSaveError] = useState<OpResult | null>(null);
  const eventId = draft.createdEventId;

  const typeFor = useCallback(() => {
    const type = catalog?.findType(draft.eventCategoryId) ?? null;

    return { type, parent: catalog?.parentOf(type) ?? null };
  }, [catalog, draft.eventCategoryId]);

  /** Leaving the essentials: CREATE the event (with its starting features), or re-save the essentials once it exists. */
  const saveEssentials = useCallback(async (): Promise<boolean> => {
    if (eventId) {
      // #139 — a quick event keeps the quick path's fixed answers (no place, a headcount) on every re-save.
      const res = await EventService.update({ id: eventId }, draft.quick ? quickEventInput(draft, false) : essentialsInput(draft, false));

      if (!res.didSucceed()) {
        setSaveError(res);

        return false;
      }

      return true;
    }

    const { type, parent } = typeFor();
    const features = initialFeatures(type, parent, catalog?.rules ?? [], draft.eventMode);
    const res = await EventService.create({ ...essentialsInput(draft, true), settings: { features: { on: features } } });

    if (!res.didSucceed()) {
      setSaveError(res);

      return false;
    }

    const created = (res.getDataFirst() as Event) ?? {};

    if (!created.id) {
      setSaveError(res);

      return false;
    }

    setDraft((d) => ({ ...d, createdEventId: created.id as string, featuresOn: d.featuresOn ?? features }));
    funnel.eventCreated({
      step: ESSENTIALS_LAST_STEP,
      eventMode: draft.eventMode,
      eventType: type?.code ?? null,
      parentType: parent?.code ?? null,
      eventFormat: draft.eventFormat,
      features: features.length,
      quick: !!draft.quick,
      quickKind: quickKindOf(draft),
    });

    return true;
  }, [eventId, draft, typeFor, catalog?.rules, funnel]);

  /** Media: the cover fields, then the highlight links (non-fatal — the event is the thing the host asked for). */
  const saveMedia = useCallback(async (): Promise<boolean> => {
    if (!eventId) {
      return true;
    }

    const res = await EventService.update({ id: eventId }, mediaInput(draft));

    if (!res.didSucceed()) {
      setSaveError(res);

      return false;
    }

    const { add, remove } = highlightChanges(draft.highlightIds, draft.highlightLinks);
    const links = { ...draft.highlightLinks };
    let highlightFailed = false;

    for (const highlightId of add) {
      const r = await EventHighlightService.create({ eventId, highlightId });
      const linkId = r.didSucceed() ? (r.getDataFirst() as { id?: string } | undefined)?.id : undefined;

      if (linkId) {
        links[highlightId] = linkId;
      } else {
        highlightFailed = true;
      }
    }

    for (const { highlightId, linkId } of remove) {
      const r = await EventHighlightService.remove(linkId);

      if (r.didSucceed()) {
        delete links[highlightId];
      } else {
        highlightFailed = true;
      }
    }

    setDraft((d) => ({ ...d, highlightLinks: links }));

    if (highlightFailed) {
      toast.error(
        t('events:wizard.highlightsFailed', {
          defaultValue: 'Some highlights could not be saved. You can change them in Details & Settings.',
        }),
      );
    }

    return true;
  }, [eventId, draft, toast, t]);

  /**
   * Features: `settings.features`, after the rules (a feature the mode cannot have is dropped, one it requires is
   * kept). ⚠ `settings` is ONE jsonb column and an update REPLACES it — so it is re-read and merged, never written bare.
   */
  const saveFeatures = useCallback(async (): Promise<boolean> => {
    if (!eventId) {
      return true;
    }

    const { type, parent } = typeFor();
    const on = featuresAfterRules(type, parent, catalog?.rules ?? [], draft.eventMode, draft.featuresOn ?? []);
    const current = await EventService.get(eventId);

    if (!current.didSucceed()) {
      setSaveError(current);

      return false;
    }

    const existing = (current.getDataFirst() as Event | undefined)?.settings ?? null;
    const res = await EventService.update({ id: eventId }, { settings: withFeatures(existing, on) });

    if (!res.didSucceed()) {
      setSaveError(res);

      return false;
    }

    setDraft((d) => ({ ...d, featuresOn: on }));

    return true;
  }, [eventId, draft.eventMode, draft.featuresOn, typeFor, catalog?.rules]);

  /**
   * #62 P6(d)–(d3) — the website step, onto the HOME page: the ticked blocks added, the description written into the About
   * block (an ordinary rich-text block — d2), and the whole page put in the host's order (d3). What may be added is
   * re-derived from the draft (a feature switched off since, a map with no venue, an About with nothing written), and what
   * is already on the site is skipped by `applyWebsitePlan`, which re-reads it — so a second Next adds nothing twice.
   * ⚠ What it made is written BACK to the draft (the About block's id, the order as `w:<id>` items), so a revisit edits
   *   that block and shows that order rather than adding another.
   */
  const saveWebsite = useCallback(async (): Promise<boolean> => {
    if (!eventId) {
      return true;
    }

    const description = draft.description ?? '';

    // ⚠ Which features have content decides what may be ADDED (an empty feature block would publish the host's own
    //   placeholder to guests). A failed read falls back to the step's ticks — it only ever offered non-empty ones.
    const usageRes = await EventService.featureUsage(eventId);
    const usage = usageRes.didSucceed() ? (usageRes.getDataFirst() as EventFeatureUsage | undefined) : undefined;
    // ⚠ The Schedule block only WITH sessions (the Schedule step may have added none) — any mode but a repeating one.
    const sessionsRes = draft.scheduleMode !== SCHEDULE_MODE.RECURRING ? await EventTimeSlotService.list({ eventId: [eventId] }, { page: 1, pageSize: 1 }) : null;
    const hasSessions = !!sessionsRes?.didSucceed() && Array.isArray(sessionsRes.data) && sessionsRes.data.length > 0;
    const rows = planWebsiteBlocks({
      featuresOn: draft.featuresOn ?? [],
      withData: usage ? usage.withData ?? [] : draft.featuresOn ?? [],
      hasDescription: !!description.trim(),
      hasStart: !!draft.startsAt,
      hasVenue: !!draft.venueId,
      hasSessions: draft.scheduleMode !== SCHEDULE_MODE.RECURRING && hasSessions,
      onSite: new Set(),
    });
    const keys = websiteKeysToAdd(rows, draft.websiteBlocks ?? []);
    const touched = keys.length > 0 || draft.websiteOrder != null || (!!draft.aboutWidgetId && !!draft.descriptionEdited);

    if (!touched) {
      return true;
    }

    const result = await applyWebsitePlan(eventId, {
      keys,
      order: draft.websiteOrder ?? null,
      about:
        draft.description != null
          ? {
              content: description,
              textKind: textKindOfFormat(draft.descriptionFormat),
              widgetId: draft.aboutWidgetId ?? null,
              edited: !!draft.descriptionEdited,
            }
          : null,
      // #140 — a Sign-up list chosen on the Gifts step is the event's GIFT list, and its block says so.
      titleOf: (key, fallback) =>
        key === 'signup' && isGiftList({ giftSignup: draft.giftSignup, featuresOn: draft.featuresOn })
          ? t('events:wizard.gifts.listBlockTitle', { defaultValue: 'Gift list' })
          : t(`events:wizard.website.blockTitle.${key}`, { defaultValue: fallback }),
      subtitleOf: (key, fallback) => t(`events:wizard.website.blockSubtitle.${key}`, { defaultValue: fallback, title: draft.title.trim() }),
      homeName: t('events:wizard.website.homeName', { defaultValue: 'Home' }),
    });

    // ⚠ Kept even on a refusal: what was made before it is real, and a retry must not make it again — and must put it
    //   where the host dragged it. ⚠ After a SUCCESS the order is forgotten: the page now holds it, and a later visit
    //   shows the page as it is (the host may reorder it in the editor meanwhile), applying only a drag made then.
    setDraft((d) => ({
      ...d,
      aboutWidgetId: result.aboutWidgetId ?? d.aboutWidgetId ?? null,
      descriptionEdited: result.error ? d.descriptionEdited : false,
      websiteOrder: result.error ? orderAfterCreate(d.websiteOrder, result.created) : null,
    }));

    if (result.error) {
      setSaveError(result.error);

      return false;
    }

    return true;
  }, [
    eventId,
    draft.description,
    draft.descriptionFormat,
    draft.descriptionEdited,
    draft.aboutWidgetId,
    draft.websiteOrder,
    draft.featuresOn,
    draft.startsAt,
    draft.venueId,
    draft.scheduleMode,
    draft.websiteBlocks,
    draft.title,
    draft.giftSignup,
    t,
  ]);

  // #140 — a Sign-up list switched off (on the Features step, or by a mode the rules refuse it) stops being the gift list:
  // switched back on later it is an ordinary list again, until the Gifts step makes it the gift list.
  useEffect(() => {
    if (draft.giftSignup && !(draft.featuresOn ?? []).includes(SIGNUP_LIST_FEATURE)) {
      setDraft((d) => ({ ...d, giftSignup: null, giftFeatureEdited: false }));
    }
  }, [draft.giftSignup, draft.featuresOn]);

  /**
   * The Schedule step (#62 P6, owner 2026-09-30): a repeating event must HAVE dates. The rule is saved and the dates made
   * when it changed on the step, or when the event has none — read, not remembered (`ensureRecurringDates`). An agenda's
   * sessions are saved by their own drawer, so they need nothing here.
   * ⚠ Also from FINISH on any step (review, 2026-09-30): a host who switched to "Repeats" on Basics and finished there
   *   never saw this step, and the event went out with nothing to reply to.
   */
  const saveSchedule = useCallback(async (): Promise<boolean> => {
    if (!eventId) {
      return true;
    }

    // ⓘ Not repeating (any more): leaving the step means the host has seen any leftover repeating dates — with the
    //   offer to remove them — so the step stops being forced on them (`repeatDatesMade`).
    if (draft.scheduleMode !== SCHEDULE_MODE.RECURRING) {
      if (draft.repeatDatesMade) {
        setDraft((d) => ({ ...d, repeatDatesMade: false }));
      }

      return true;
    }

    const { result, rule } = await ensureRecurringDates(eventId, draft.recurrence ?? null, !!draft.recurrenceEdited);

    if (!result.didSucceed()) {
      setSaveError(result);

      return false;
    }

    setDraft((d) => ({ ...d, recurrence: rule ?? d.recurrence ?? null, recurrenceEdited: false, repeatDatesMade: true }));

    return true;
  }, [eventId, draft.scheduleMode, draft.recurrence, draft.recurrenceEdited, draft.repeatDatesMade]);

  /**
   * #140 — the Gifts step: the features first when the gift list switched the Sign-up list on or off (its step follows),
   * then the registry block — made, rewritten, or removed when every link is gone (`applyGiftLinks`).
   * ⚠ A half-filled registry row holds Next: it is marked at its own inputs (`giftLinksTried`) and named at the top —
   *   never dropped on save (#85's lesson).
   */
  const saveGifts = useCallback(async (): Promise<boolean> => {
    if (!eventId) {
      return true;
    }

    const links = draft.giftLinks ?? [];

    if (Object.keys(giftLinkProblems(links)).length > 0) {
      setDraft((d) => ({ ...d, giftLinksTried: true }));
      setSaveError(
        new OpResult({ code: OP_RESULT_CODES.VALIDATION_FAILED }).addError(
          '',
          t('events:wizard.gifts.rowsIncomplete', { defaultValue: 'Some registries are missing a name or a web address — check the marked rows below.' }),
        ),
      );

      return false;
    }

    if (draft.giftFeatureEdited) {
      if (!(await saveFeatures())) {
        return false;
      }

      setDraft((d) => ({ ...d, giftFeatureEdited: false }));
    }

    if (!draft.giftLinksEdited) {
      return true;
    }

    const result = await applyGiftLinks(eventId, {
      buttons: giftLinkButtons(links),
      widgetId: draft.giftLinksWidgetId ?? null,
      title: t('events:wizard.gifts.registryBlockTitle', { defaultValue: 'Gift registry' }),
      homeName: t('events:wizard.website.homeName', { defaultValue: 'Home' }),
    });

    // ⚠ The block as it now stands is kept even on a refusal — a retry rewrites it, never makes a second one.
    setDraft((d) => ({
      ...d,
      giftLinksWidgetId: result.widgetId,
      giftLinksEdited: result.error ? d.giftLinksEdited : false,
      giftLinksTried: false,
    }));

    if (result.error) {
      setSaveError(result.error);

      return false;
    }

    return true;
  }, [eventId, draft.giftLinks, draft.giftFeatureEdited, draft.giftLinksEdited, draft.giftLinksWidgetId, saveFeatures, t]);

  /** Save what THIS step owns. Before the event exists, only the last essentials step writes (it creates). */
  const saveStep = useCallback(
    async (key: WizardStepKey): Promise<boolean> => {
      const index = registryIndexOf(key);

      if (index <= ESSENTIALS_LAST_INDEX) {
        return eventId || key === ESSENTIALS_LAST_STEP ? saveEssentials() : true;
      }

      if (key === 'polish') {
        return saveMedia();
      }

      if (key === 'features') {
        return saveFeatures();
      }

      if (key === 'schedule') {
        return saveSchedule();
      }

      if (key === 'website') {
        return saveWebsite();
      }

      if (key === 'gifts') {
        return saveGifts();
      }

      return true;
    },
    [eventId, saveEssentials, saveMedia, saveFeatures, saveSchedule, saveWebsite, saveGifts],
  );

  const runSave = useCallback(
    async (key: WizardStepKey): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);

      try {
        return await saveStep(key);
      } finally {
        setSaving(false);
      }
    },
    [saveStep],
  );

  const handleNext = useCallback(async () => {
    if (await runSave(stepKey)) {
      funnel.stepCompleted(stepKey);
      goNext();
    }
  }, [runSave, stepKey, goNext, funnel]);

  /** Finish from any step once the event exists: save this step, then show the done screen with the fresh event. */
  const finish = useCallback(async () => {
    if (!eventId || !(await runSave(stepKey))) {
      return;
    }

    // A repeating event finished from ANOTHER step still gets its dates (the Schedule step's own save ran above).
    if (stepKey !== 'schedule' && !(await runSave('schedule'))) {
      return;
    }

    // ⓘ Only once BOTH saves held — a refused schedule save sends the host back to press Finish again, which must not
    //   count this step complete twice.
    funnel.stepCompleted(stepKey);

    const fresh = await EventService.get(eventId);
    const event = fresh.didSucceed() ? ((fresh.getDataFirst() as Event) ?? { id: eventId }) : ({ id: eventId } as Event);

    // #62 P6(d) — what the host SAW on the website step and left unticked, captured before the draft is cleared: the finish
    // screen must not then nag about it as if it were forgotten.
    const ticked = draft.websiteBlocks;

    setWebsiteDeclined(ticked == null ? [] : WEBSITE_BLOCK_OPTIONS.map((o) => o.key).filter((key) => !ticked.includes(key)));
    setFinishedGiftList(isGiftList(draft));
    clear(); // the event exists and is set up — nothing left to restore
    funnel.finished(stepKey);
    setResult({ ok: true, event });
  }, [eventId, runSave, stepKey, clear, draft, funnel]);

  /**
   * #139 — Finish on a QUICK path: create the event (a draft, with its picture and only the features the rules require),
   * #148 make its ticket (the ticket sale), then publish it, then the finish screen with its link.
   * ⚠ Separate writes, not a create-as-published: a refused CREATE keeps the host here with the reason; a refused PUBLISH
   *   still leaves them an event, so the finish screen opens on it as a draft — with the reason and its Publish button —
   *   rather than this screen offering to create it a second time.
   * ⚠ Once the event exists (`createdEventId` is persisted — a reload mid-way) it is UPDATED, never created again; the
   *   same for the ticket (`quickTicketTypeId`): a Finish retried after its refusal updates it.
   * ⚠ #148 PUBLISHED ONLY WHEN IT CAN SELL (owner): a ticket that costs money needs card payments, asked of Stripe AGAIN
   *   here (the host may have connected in another tab since the screens checked). Otherwise it stays a draft and the
   *   finish screen says why — the page would offer a ticket nobody could pay for.
   */
  const finishQuick = useCallback(async () => {
    const kind = quickKindOf(draft) ?? QUICK_KIND.HEADCOUNT;
    const selling = kind === QUICK_KIND.TICKETS;

    // A restored draft can land past a step left unfinished (or the plan refuses tickets): back to the first such step,
    // before anything is written.
    const steps = quickStepsOf(draft);
    const unfinished = steps.find((key) => !canAdvanceFrom(key, draft)) ?? (selling && !ticketGate.canCreate ? 'tickets' : null);

    if (unfinished) {
      goToStep(unfinished);

      return;
    }

    setSaving(true);
    setSaveError(null);

    // A refusal goes to the step asking what it names (the reason is shown there, at the top and under the input).
    const refused = (res: OpResult) => {
      setSaveError(res);

      const step = quickStepOfRefusal(res, steps);

      if (step) {
        goToStep(step);
      }
    };

    try {
      // ⚠ Stripe asked AGAIN, fresh, BEFORE anything is written — and when its answer is not what the button said
      //   ("Create & start selling" over a sale that would be held, or "Create as a draft" over one that would go live),
      //   nothing is done: the button now says the new answer, and the host presses what it says (review #148).
      const priced = selling && (draft.quickTicket?.price ?? 0) > 0;
      const hold = quickPublishHold(draft, priced ? await cardPayments.check(true) : null);

      if (hold !== quickShown) {
        toast.info(
          t('events:wizard.quickTickets.paymentsChanged', {
            defaultValue: 'Card payments changed since this screen checked. The Finish button now says what it will do.',
          }),
        );

        return;
      }

      let id = eventId;

      if (id) {
        const res = await EventService.update({ id }, quickEventInput(draft, false));

        if (!res.didSucceed()) {
          refused(res);

          return;
        }
      } else {
        const { type, parent } = typeFor();
        const features = quickFeatures(type, parent, catalog?.rules ?? [], quickEventMode(kind));
        const res = await EventService.create({ ...quickEventInput(draft, true), settings: { features: { on: features } } });
        const created = res.didSucceed() ? ((res.getDataFirst() as Event | undefined) ?? null) : null;

        if (!created?.id) {
          refused(res);

          return;
        }

        id = created.id;
        setDraft((d) => ({ ...d, createdEventId: created.id as string, featuresOn: features }));
        funnel.eventCreated({
          step: stepKey,
          eventMode: quickEventMode(kind),
          eventType: type?.code ?? null,
          parentType: parent?.code ?? null,
          eventFormat: selling ? draft.eventFormat : EVENT_FORMAT.IN_PERSON,
          features: features.length,
          quick: true,
          quickKind: kind,
        });
      }

      if (selling) {
        const ticketId = draft.quickTicketTypeId;
        const res = ticketId
          ? await TicketTypeService.update(ticketId, quickTicketInput(draft))
          : await TicketTypeService.create({ eventId: id, ...quickTicketInput(draft) });
        const ticket = res.didSucceed() ? ((res.getDataFirst() as TicketType | undefined) ?? null) : null;

        if (!ticket?.id) {
          // ⓘ The event is made (and remembered): the host fixes the ticket where it is asked, and Finish again.
          setSaveError(res);
          goToStep(quickStepOfRefusal(res, steps) ?? 'tickets');

          return;
        }

        setDraft((d) => ({ ...d, quickTicketTypeId: ticket.id as string }));
      }

      let publishError: string | null = null;

      // #198 a — not for a publisher whose email is unconfirmed (a new event has never been public, so the server would
      // refuse it): the finish screen opens on the draft and says how to publish it.
      if (!hold && user?.emailConfirmation === true) {
        const published = await EventService.update({ id }, { eventStatus: EVENT_STATUS_PUBLISHED });

        // ⚠ Every message, named or not: the server names a publish refusal (`plan`, `slug`…), which getErrorSummary() drops.
        publishError = published.didSucceed()
          ? null
          : refusalReason(published) || t('events:wizard.done.publishFailed', { defaultValue: 'Could not publish the event. Please try again.' });
      }

      funnel.stepCompleted(stepKey);

      const fresh = await EventService.get(id);
      const event = fresh.didSucceed() ? ((fresh.getDataFirst() as Event) ?? ({ id } as Event)) : ({ id } as Event);

      setQuickPublishError(publishError);
      setQuickHold(hold);
      clear(); // the event exists — nothing left to restore
      funnel.finished(stepKey);
      setResult({ ok: true, event });
    } finally {
      setSaving(false);
    }
  }, [eventId, draft, typeFor, catalog?.rules, funnel, stepKey, clear, t, goToStep, ticketGate.canCreate, cardPayments, quickShown, toast, user?.emailConfirmation]);

  /** The Features step's "Sell tickets instead": the host's own choice, saved to the event at once. */
  const switchEventMode = useCallback(
    async (mode: EventMode): Promise<void> => {
      const previous = draft.eventMode;

      setDraft((d) => ({ ...d, eventMode: mode }));

      if (!eventId) {
        return;
      }

      setSaving(true);
      setSaveError(null);

      const res = await EventService.update({ id: eventId }, { eventMode: mode });

      setSaving(false);

      if (!res.didSucceed()) {
        setDraft((d) => ({ ...d, eventMode: previous }));
        setSaveError(res);
      }
    },
    [draft.eventMode, eventId],
  );

  // ── Restore prompt actions ────────────────────────────────────────────────
  const handleRestore = useCallback(() => {
    if (savedState) {
      setDraft(savedState.draft);
      goToStep(savedState.stepKey);
      setResumed(true);
    }
    setRestoreOpen(false);
  }, [savedState, goToStep]);

  const handleStartOver = useCallback(() => {
    clear();
    // ⚠ "Start over" means a fresh draft, which is a draft WITH the account's defaults — not a blank
    // one. Otherwise starting over would silently be a worse starting point than opening the wizard.
    setDraft(applyAccountDefaults(emptyEventWizardDraft(), accountPreference?.preference));
    seededRef.current = true;
    setCurrentKey('type');
    setRestoreOpen(false);
  }, [clear, accountPreference?.preference]);

  const runExclusive = useCallback(async <T,>(work: () => Promise<T>): Promise<T> => {
    setSaving(true);
    setSaveError(null);

    try {
      return await work();
    } finally {
      setSaving(false);
    }
  }, []);

  const contextValue: EventWizardContextValue = useMemo(
    () => ({
      draft,
      patchDraft,
      stepIndex,
      stepKey,
      // #148 — a plan that refuses tickets stops the quick ticket screen too (Finish could not make the ticket).
      canAdvance: canAdvanceFrom(stepKey, draft) && !(stepKey === 'tickets' && ticketsRefused),
      goNext,
      goBack,
      goToStep,
      isDirty,
      createdEventId: eventId,
      saving,
      switchEventMode,
      runExclusive,
      saveError,
      cardPayments,
    }),
    [draft, patchDraft, stepIndex, stepKey, goNext, goBack, goToStep, isDirty, eventId, saving, switchEventMode, runExclusive, saveError, ticketsRefused, cardPayments],
  );

  const isLastStep = stepIndex === totalSteps - 1;
  // #148 — what the quick Finish will do, said on its button: a ticket sale that cannot take payment yet is kept a draft,
  // and until the check answers nothing is claimed (the button waits for it).
  const quickChecking = quickShown === QUICK_FINISH_CHECKING;
  const quickHeld = !!quickShown && !quickChecking;
  const quickFinishLabel =
    quickKind !== QUICK_KIND.TICKETS
      ? t('events:wizard.quick.finish', { defaultValue: 'Create & get the link' })
      : quickChecking
        ? t('events:wizard.quickTickets.finishChecking', { defaultValue: 'Checking card payments…' })
        : quickHeld
          ? t('events:wizard.quickTickets.finishDraft', { defaultValue: 'Create as a draft' })
          : t('events:wizard.quickTickets.finish', { defaultValue: 'Create & start selling' });
  const progressValue = ((stepIndex + 1) / totalSteps) * 100;

  const renderStep = () => {
    switch (stepKey) {
      case 'type':
        return <EventTypeStep />;
      case 'basics':
        return <BasicsStep />;
      case 'location':
        return <WhereStep />;
      case 'guests':
        return <GuestsStep />;
      case 'vouchers':
        return <VouchersStep />;
      case 'tickets':
        // #148 — on the quick ticket sale the event does not exist yet: its own screen asks the one ticket.
        return quickKind === QUICK_KIND.TICKETS ? <QuickTicketStep /> : <TicketsStep />;
      case 'questions':
        return <QuestionsStep />;
      case 'policies':
        return <PoliciesStep />;
      case 'invites':
        return <InvitesStep />;
      case 'gifts':
        return <GiftsStep />;
      case 'signups':
        return <SignupsStep />;
      case 'polls':
        return <PollsStep />;
      case 'games':
        return <GamesStep />;
      case 'draws':
        return <DrawsStep />;
      case 'artists':
        return <ArtistsStep />;
      case 'pois':
        return <PoisStep />;
      case 'accommodations':
        return <AccommodationsStep />;
      case 'volunteers':
        return <VolunteersStep />;
      case 'partners':
        return <PartnersStep />;
      case 'timeline':
        return <TimelineStep />;
      case 'tasks':
        return <TasksStep />;
      case 'budget':
        return <BudgetStep />;
      case 'schedule':
        return <ScheduleStep />;
      case 'website':
        return <WebsiteStep />;
      case 'channels':
        return <ChannelsStep />;
      case 'polish':
        return <MediaStep />;
      case 'features':
        return <FeaturesStep />;
      default:
        return <PlaceholderStep label={t(stepMeta.labelKey, { defaultValue: stepMeta.labelDefault })} />;
    }
  };

  if (result) {
    return <EventCreatedScreen result={result} onRetry={() => setResult(null)} websiteDeclined={websiteDeclined} giftList={finishedGiftList} publishError={quickPublishError} publishHold={quickHold} />;
  }

  return (
    <WizardProvider value={contextValue}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        {/* Header */}
        <Box
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            backgroundColor: 'var(--mantine-color-body)',
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Container size="sm" px="md" py="sm">
            <Group justify="space-between" wrap="nowrap" mb="xs">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Text size="xs" c="dimmed">
                  {t('events:wizard.stepCounter', {
                    defaultValue: 'Step {{current}} of {{total}}',
                    current: stepIndex + 1,
                    total: totalSteps,
                  })}
                </Text>
                <Title order={4} lh={1.2} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t(stepMeta.labelKey, { defaultValue: stepMeta.labelDefault })}
                </Title>
              </Stack>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                aria-label={t('common:actions.close', { defaultValue: 'Close' })}
                onClick={() => setExitOpen(true)}
              >
                <AppIcon iconName="X" size={20} />
              </ActionIcon>
            </Group>
            <Progress value={progressValue} size="sm" radius="xl" color="brand" />
          </Container>
        </Box>

        {/* Body */}
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          <Container size="sm" px="md" py="lg">
            <Stack gap="md">
              {/* ⓘ Not on the quick path: Finish there publishes, so "saved as a draft… finish at any step" is not true. */}
              {eventId && !draft.quick && registryIndexOf(stepKey) > ESSENTIALS_LAST_INDEX ? (
                <Alert variant="light" color="teal" icon={<AppIcon iconName="Check" size={18} />}>
                  <Text size="md">
                    {t('events:wizard.savedAsDraft', {
                      defaultValue: 'Your event is saved as a draft. Add more now, or finish at any step.',
                    })}
                  </Text>
                </Alert>
              ) : null}
              {/* ⚠ The steps reuse event pages' drawers, which price amounts in "the current event's" currency — and this
                  route names no event. So the steps sit under a provider scoped to the CREATED one (#62 P6(f)). It sits
                  at one place in the tree, so it is not re-mounted (nor the event re-read) on every step. */}
              <CurrentEventProvider eventId={eventId}>{renderStep()}</CurrentEventProvider>
            </Stack>
          </Container>
        </Box>

        {/* Footer */}
        <Box
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 5,
            backgroundColor: 'var(--mantine-color-body)',
            borderTop: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Container size="sm" px="md" py="sm">
            {/* ⚠ A refused save is shown HERE, beside the button that sent it (owner, 2026-10-22). At the top of the step
                it sat a screen above "Create event" — a new account's first event was refused for a paid option, nothing
                visible changed, and the button looked broken. The footer is sticky, so this is always in view; a long
                message scrolls inside its own box rather than pushing the buttons off screen. A field the step owns
                still shows its own message under that input. */}
            {saveError ? (
              <Box mb="sm" style={{ maxHeight: '40dvh', overflowY: 'auto' }}>
                <ErrorAlert
                  error={saveError}
                  title={
                    eventId
                      ? t('events:wizard.saveFailed', { defaultValue: 'Could not save this step' })
                      : t('events:wizard.createFailed', { defaultValue: 'Could not create the event' })
                  }
                  page="Create event"
                  operation={eventId ? 'eventUpdate' : 'eventCreate'}
                  onDismiss={() => setSaveError(null)}
                />
              </Box>
            ) : null}
            <Group justify="space-between" wrap="nowrap">
              <Button
                variant="default"
                size="md"
                leftSection={<AppIcon iconName="ChevronLeft" size={16} />}
                onClick={goBack}
                disabled={stepIndex === 0 || saving}
              >
                {t('common:actions.back', { defaultValue: 'Back' })}
              </Button>
              <Group gap="sm" wrap="nowrap">
                {/* #62 — once the event exists the host can finish from ANY step (it saves this step first). */}
                {eventId && !isLastStep ? (
                  // #139 — a quick event (restored after its create) finishes the quick way: published, with its link.
                  <Button
                    variant="subtle"
                    size="md"
                    onClick={() => void (draft.quick ? finishQuick() : finish())}
                    disabled={saving || !contextValue.canAdvance || (draft.quick && quickChecking)}
                  >
                    {t('events:wizard.finish', { defaultValue: 'Finish' })}
                  </Button>
                ) : null}
                {isLastStep ? (
                  <Button
                    size="md"
                    // #139 — the quick path creates AND publishes here, so the button says what it does.
                    onClick={() => void (draft.quick ? finishQuick() : finish())}
                    loading={saving}
                    disabled={!contextValue.canAdvance || saving || (draft.quick && quickChecking)}
                    leftSection={<AppIcon iconName={draft.quick && !quickHeld ? 'Send' : 'Check'} size={16} />}
                  >
                    {draft.quick ? quickFinishLabel : t('events:wizard.finish', { defaultValue: 'Finish' })}
                  </Button>
                ) : (
                  <Button
                    size="md"
                    rightSection={<AppIcon iconName="ChevronRight" size={16} />}
                    onClick={() => void handleNext()}
                    loading={saving}
                    disabled={!contextValue.canAdvance || saving}
                  >
                    {stepKey === ESSENTIALS_LAST_STEP && !eventId
                      ? t('events:wizard.createAndContinue', { defaultValue: 'Create event' })
                      : t('common:actions.next', { defaultValue: 'Next' })}
                  </Button>
                )}
              </Group>
            </Group>
          </Container>
        </Box>
      </Box>

      {/* Restore-or-start-over (shown once on mount when a saved draft exists). */}
      <StackedModal
        opened={restoreOpen}
        onClose={handleStartOver}
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        centered
        title={t('events:wizard.restore.title', { defaultValue: 'Continue where you left off?' })}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('events:wizard.restore.message', {
              defaultValue: 'We found an event you started but didn’t finish. Pick up where you left off, or start over.',
            })}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleStartOver}>
              {t('events:wizard.restore.startOver', { defaultValue: 'Start over' })}
            </Button>
            <Button onClick={handleRestore}>
              {t('events:wizard.restore.restore', { defaultValue: 'Continue' })}
            </Button>
          </Group>
        </Stack>
      </StackedModal>

      {/* Confirm leaving the wizard. */}
      <ConfirmModal
        opened={exitOpen}
        onClose={() => setExitOpen(false)}
        onConfirm={confirmLeave}
        type="warning"
        title={t('events:wizard.exit.title', { defaultValue: 'Leave event setup?' })}
        message={
          eventId
            ? t('events:wizard.exit.messageCreated', {
                defaultValue: 'Your event is saved as a draft — you can finish setting it up from its page.',
              })
            : t('events:wizard.exit.message', {
                defaultValue: 'Your progress is saved — you can pick up where you left off next time.',
              })
        }
        confirmLabel={t('events:wizard.exit.confirm', { defaultValue: 'Leave' })}
        cancelLabel={t('events:wizard.exit.cancel', { defaultValue: 'Keep editing' })}
      />
    </WizardProvider>
  );
};
